<script setup lang="ts">
import { computed } from 'vue'

import type { ReportColumn } from '../../../models/column.types'
import type { ReportRow } from '../../../models/report.types'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'

type DeltaState = 'positive' | 'negative' | 'zero' | 'empty'
type DeltaTone = 'success' | 'danger' | 'neutral'

const props = defineProps<{
  row: ReportRow
  column: ReportColumn
}>()

const { t } = useI18n()
const format = useFormat()
const value = computed(() => {
  const candidate = props.row[props.column.field]
  return typeof candidate === 'number' && Number.isFinite(candidate) ? candidate : null
})
const state = computed<DeltaState>(() => {
  if (value.value === null) return 'empty'
  if (value.value === 0) return 'zero'
  return value.value > 0 ? 'positive' : 'negative'
})
// `change_type: null` is an explicit neutral metric (`notr`); an unpublished polarity falls back
// to v3's higher-is-better default (`OrphexTd.vue` `changeColorClass`).
const tone = computed<DeltaTone>(() => {
  if (value.value === null || value.value === 0 || props.column.change_type === null)
    return 'neutral'
  const improves = props.column.change_type === 'negative' ? value.value < 0 : value.value > 0
  return improves ? 'success' : 'danger'
})
const TONE_CLASS: Record<DeltaTone, string> = {
  success: 'text-success',
  danger: 'text-danger',
  neutral: 'text-muted',
}
const displayValue = computed(() =>
  format.formatPercent(value.value, { scale: 'ratio', signDisplay: 'exceptZero' }),
)
const accessibilityLabel = computed(() => {
  if (state.value === 'empty') return t('reports.table.deltaUnavailable')
  const key =
    state.value === 'positive'
      ? 'deltaIncrease'
      : state.value === 'negative'
        ? 'deltaDecrease'
        : 'deltaNoChange'
  return t(`reports.table.${key}`, { value: displayValue.value })
})
</script>

<template>
  <span
    class="report-delta-cell tabular-nums"
    :class="[`report-delta-cell--${state}`, `report-delta-cell--${tone}`, TONE_CLASS[tone]]"
    :aria-label="accessibilityLabel"
  >
    <span aria-hidden="true">{{ displayValue }}</span>
  </span>
</template>
